import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { DialogHost } from "@/lib/dialogs";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import SetPassword from "@/pages/SetPassword";
import Dashboard from "@/pages/Dashboard";
import SettingsTemplates from "@/pages/SettingsTemplates";
import SettingsPipeline from "@/pages/SettingsPipeline";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Services from "@/pages/Services";
import PricingComponents from "@/pages/PricingComponents";
import PricingHelpCentre from "@/pages/PricingHelpCentre";
import EngagerConfig from "@/pages/EngagerConfig";
import EngagerBoard from "@/pages/EngagerBoard";
import MyTasks from "@/pages/MyTasks";
import Proposals from "@/pages/Proposals";
import ProposalBuilder from "@/pages/ProposalBuilder";
import ProposalDetail from "@/pages/ProposalDetail";
import AdjustmentReport from "@/pages/AdjustmentReport";
import Admin from "@/pages/Admin";
import Templates from "@/pages/Templates";
import TemplateEditor from "@/pages/TemplateEditor";
import ProposalDocument from "@/pages/ProposalDocument";
import PublicProposal from "@/pages/PublicProposal";
import OnboardingForm from "@/pages/OnboardingForm";
import OnboardingQuestions from "@/pages/OnboardingQuestions";
import ClearanceTemplate from "@/pages/ClearanceTemplate";
import PricingTriggers from "@/pages/PricingTriggers";
import RequirementBlocks from "@/pages/RequirementBlocks";
import ClearanceDownload from "@/pages/ClearanceDownload";
import OnboardingTracker from "@/pages/OnboardingTracker";
import Pipeline from "@/pages/Pipeline";
import Reports from "@/pages/Reports";
import NeedsAttention from "@/pages/NeedsAttention";

function Protected({ children }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready || user === null)
    return <div className="min-h-screen grid place-items-center text-cass-slate">Loading…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/set-password/:token" element={<SetPassword />} />
            <Route path="/p/:token" element={<PublicProposal />} />
            <Route path="/onboard/:token" element={<OnboardingForm />} />
            <Route path="/clearance/:token" element={<ClearanceDownload />} />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:id/pricing" element={<PricingComponents />} />
              <Route path="/services/:id/onboarding" element={<OnboardingQuestions />} />
              <Route path="/pricing-help" element={<PricingHelpCentre />} />
              <Route path="/engager" element={<EngagerConfig />} />
              <Route path="/engager/board" element={<EngagerBoard />} />
              <Route path="/engager/my-tasks" element={<MyTasks />} />
              <Route path="/proposals" element={<Proposals />} />
              <Route path="/pipeline" element={<Pipeline />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/needs-attention" element={<NeedsAttention />} />
              <Route path="/onboarding-tracker" element={<OnboardingTracker />} />
              <Route path="/proposals/new" element={<ProposalBuilder />} />
              <Route path="/proposals/:id" element={<ProposalDetail />} />
              <Route path="/reports/adjustments" element={<AdjustmentReport />} />
              <Route path="/proposals/:id/edit" element={<ProposalBuilder />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/templates" element={<SettingsTemplates />} />
              <Route path="/admin/pipeline" element={<SettingsPipeline />} />
              <Route path="/admin/onboarding-questions" element={<OnboardingQuestions />} />
              <Route path="/admin/clearance-template" element={<ClearanceTemplate />} />
              <Route path="/admin/pricing-triggers" element={<PricingTriggers />} />
              <Route path="/admin/requirement-blocks" element={<RequirementBlocks />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/templates/:id" element={<TemplateEditor />} />
              <Route path="/proposals/:id/document" element={<ProposalDocument />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
          <DialogHost />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
